/**
 * №53 (53.2) — WORKFORCE, THE BOARD.
 *
 * Drawn in `docs/53-mockups/workforce-1.png` (approved): four stat tiles, then a
 * card per hire in two columns. Transferred from the crew prototype's `.stats` /
 * `.hires` — the chrome lives in `app-chrome.css`, this file is the data.
 *
 * ## The two honesty rules the drawing states on its own caption
 *
 * ① **The "Now:" line always carries a timestamp.** A live claim without one is
 *    unfalsifiable. The engine sets `verb` only while a run is genuinely executing
 *    and reports a dead run as `stale_run` rather than as available (№22.4.1 F1),
 *    so this surface renders the verb ONLY alongside its `since` — never one
 *    without the other.
 *
 * ② **Hours returned renders absent, not zero.** The number arrives with Today
 *    (53.5). A dashed tile saying `not yet` is honest; a "0.0h" tile is a claim
 *    the product cannot walk back (D-CR2).
 *
 * ## The status vocabulary, settled here
 *
 * The engine returns `working | awaiting | available`; the board wants
 * `working · needs you · blocked · waiting`. Three map, and the fourth is the
 * interesting one:
 *
 *   working   ← status === "working"
 *   needs you ← this hire has an open card (`open_cards > 0`) — it OUTRANKS the
 *               engine's tier, because a hire waiting on your judgement is the
 *               thing the board exists to surface
 *   blocked   ← an unresolved `blocked_events` row (`blocked`)
 *   waiting   ← status === "awaiting"
 *   available → **NO PILL AT ALL.** There is no crew word for it, and inventing
 *               "idle" for a staff room is the wrong instinct — a person reads it
 *               as an accusation. The "Now:" line says what they last finished
 *               instead, which is the useful half of the same fact.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { mcpClient } from "../../../lib/mcp-client";
import { NEEDS_YOU_KEY } from "../../hooks/needs-you-key";
import { avatarColorFor } from "./staff-desk";
import { boardPill, clockOf } from "./workforce-board-model";
import { JobDescriptionModal } from "./JobDescriptionModal";

export function WorkforceBoardPage() {
  const wsInfo = useWorkspaceInfo();
  // The roster, read exactly as the shell reads it — library-root agents, minus
  // the workspace's own root node (which is not a hire).
  const roster = useMemo(
    () => (wsInfo.data?.library_root ?? [])
      .filter((n: { type: string; is_root?: boolean }) => n.type === "agent" && !n.is_root)
      .map((n: { id: string; name: string }) => ({ id: n.id, name: n.name })),
    [wsInfo.data?.library_root],
  );
  const ids = useMemo(() => roster.map((r: { id: string }) => r.id), [roster]);
  const status = useWorkforceStatus(ids, ids.length > 0);

  // The decisions feed, off the SAME cache entry the rail badge and Needs-you use
  // (react-query dedupes by key), so the board's "decisions waiting on you" tile
  // can never disagree with the number in the rail.
  const cards = useQuery({
    queryKey: NEEDS_YOU_KEY,
    queryFn: () => mcpClient.listNeedsYou(),
    staleTime: 5_000,
    select: (f) => f.tasks,
  });
  const openCards = (cards.data ?? []).filter((t) => (t.needs_you_reason ?? "").trim().length > 0);

  // The job description opens over the board rather than routing away: it is
  // something you read ABOUT a hire while looking at the board, not a place.
  const [reading, setReading] = useState<{ id: string; name: string } | null>(null);

  const stats = useMemo(() => {
    const all = Object.values(status.data ?? {});
    return {
      done: all.reduce((n, s) => n + (s.tasks_done_today ?? 0), 0),
      matters: all.reduce((n, s) => n + (s.matters_carried ?? 0), 0),
      blocked: all.filter((s) => s.blocked).length,
    };
  }, [status.data]);

  const lead = roster.length
    ? `${roster.length} hire${roster.length === 1 ? "" : "s"} carrying ${stats.matters} matter${stats.matters === 1 ? "" : "s"} between them.${stats.blocked ? ` ${stats.blocked} ${stats.blocked === 1 ? "is" : "are"} blocked and ${stats.blocked === 1 ? "has" : "have"} told you why.` : ""}`
    : "No hires yet. A hire is a role, not a feature — it starts the moment you hire it.";

  return (
    <div className="screen">
      <div className="viewhead">
        <h2>Workforce</h2>
        <p className="lead" data-testid="table-caption">{lead}</p>
      </div>

      <div className="stats" data-testid="workforce-stats">
        <div className="stat"><b>{stats.done}</b><span>tasks done today</span></div>
        {/* ② — the drawn dashed tile. It stays dashed until 53.5 gives the number
            an honest source; a "0.0h" here would be the one lie on the page. */}
        <div className="stat stat--absent" data-testid="stat-hours-returned">
          <b>not yet</b><span>lawyer hours returned this week</span>
        </div>
        <div className={`stat${openCards.length ? " stat--alert" : ""}`}>
          <b>{openCards.length}</b><span>decision{openCards.length === 1 ? "" : "s"} waiting on you</span>
        </div>
        <div className="stat"><b>{stats.blocked}</b><span>hire{stats.blocked === 1 ? "" : "s"} blocked</span></div>
      </div>

      <div className="hires" data-testid="workforce-hires">
        {roster.map((h: { id: string; name: string }) => {
          const st = status.data?.[h.id];
          const pill = boardPill(st);
          const since = clockOf(st?.since);
          const cardsFor = openCards.filter((t) => t.agent_id === h.id);
          const meta = [
            st?.tasks_done_today != null ? `done today ${st.tasks_done_today}` : null,
            // ENTRIES, never hours — see the engine's note. Absent at zero rather
            // than a "0 entries" line that says nothing.
            st?.time_entries_today ? `time entries ${st.time_entries_today}` : null,
            st?.open_cards != null ? `cards ${st.open_cards}` : null,
          ].filter(Boolean) as string[];

          return (
            <div className="hire" key={h.id} data-testid="hire-card" data-hire={h.id}>
              <div className="hire__who">
                {/* crew `.hire .av` is a SOFT fill with the hue as the text — not a
                    saturated block. The hue itself stays per-hire (RC1-1: one
                    identity-colour derivation, a hire is one colour on every
                    surface), so this is crew's treatment of the repo's ruling
                    rather than a choice between them. */}
                <span
                  className="hire__av"
                  aria-hidden="true"
                  style={{
                    background: `color-mix(in srgb, ${avatarColorFor(h.id, roster)} 16%, var(--surface-1))`,
                    color: avatarColorFor(h.id, roster),
                  }}
                >
                  {h.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="hire__id">
                  <b>{h.name}</b>
                  <span>
                    {st?.matters_carried ? `carrying ${st.matters_carried} matter${st.matters_carried === 1 ? "" : "s"}` : "no matters yet"}
                  </span>
                </span>
                {pill && <span className={`pill pill--${pill.tone}`} data-testid="hire-pill" style={{ marginLeft: "auto" }}>{pill.label}</span>}
              </div>

              {/* ① — the verb and its timestamp ship together or not at all. */}
              {st?.verb && since ? (
                <div className="hire__now" data-testid="hire-now">
                  <span>{st.verb}</span><span className="since">since {since}</span>
                </div>
              ) : st?.stale_run ? (
                <div className="hire__now" data-testid="hire-now">
                  <em>a run is open but has not reported — treating it as stopped</em>
                </div>
              ) : null}

              <div className="hire__meta">{meta.map((m) => <span key={m}>{m}</span>)}</div>

              <div className="hire__act">
                {cardsFor.length > 0 && <Link to="/app" data-testid="hire-open-card">Open the card →</Link>}
                {cardsFor.length > 0 && <span style={{ color: "var(--text-3)", margin: "0 8px" }}>·</span>}
                <button type="button" onClick={() => setReading({ id: h.id, name: h.name })} data-testid="hire-read-jd">
                  Read the job description →
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {reading && (
        <JobDescriptionModal
          agentId={reading.id}
          name={reading.name}
          onClose={() => setReading(null)}
        />
      )}
    </div>
  );
}
